import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { OtpOutcome } from "./types";
import { LanguageToggle } from "./LanguageToggle";

export interface PhoneEntryStepProps {
  onSubmit: (
    phoneNumber: string,
    marketingConsent: boolean,
  ) => Promise<OtpOutcome>;
  isSubmitting: boolean;
  /** Show the language toggle. Defaults to true. */
  showLanguageToggle?: boolean | undefined;
  /** When provided, renders a close (X) button beside the toggle. */
  onClose?: (() => void) | undefined;
  /** Override the heading ("Enter your phone number" by default). */
  title?: string | undefined;
  /** Override the description shown under the heading. */
  description?: string | undefined;
  /** Override the phone field label ("Phone number" by default). */
  fieldLabel?: string | undefined;
  /** Show the marketing opt-in checkbox. Defaults to true. */
  showMarketingConsent?: boolean | undefined;
  /** Override the footer disclosure text. */
  footerText?: React.ReactNode | undefined;
  /** Render flat content (no card chrome) when embedded in a modal dialog. */
  bare?: boolean | undefined;
}

const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export const isValidPhoneNumber = (raw: string): boolean =>
  E164_PATTERN.test(raw.trim());

export const normalizePhoneNumber = (raw: string): string => {
  const withoutSeparators = raw.trim().replace(/[\s()-]/g, "");
  return withoutSeparators.startsWith("+")
    ? withoutSeparators
    : `+${withoutSeparators}`;
};

const RATE_LIMITED_CODE = "OT_005";
const RETRY_WAIT_NOT_REACHED_CODE = "OT_006";

export const PhoneEntryStep: React.FC<PhoneEntryStepProps> = ({
  onSubmit,
  isSubmitting,
  showLanguageToggle = true,
  onClose,
  title,
  description,
  fieldLabel,
  showMarketingConsent = true,
  footerText,
  bare = false,
}) => {
  const { t } = useTranslation("sharedLogin");
  const headingText = title ?? t("phone.heading");
  const subtitleText = description ?? t("phone.subtitle");
  const fieldLabelText = fieldLabel ?? t("phone.phoneLabel");
  const footer = footerText ?? t("phone.disclosure");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showFormatError, setShowFormatError] = useState(false);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | undefined>(
    undefined,
  );
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const normalized = normalizePhoneNumber(phoneNumber);
    if (!isValidPhoneNumber(normalized)) {
      setShowFormatError(true);
      return;
    }
    setShowFormatError(false);
    setSubmitErrorCode(undefined);
    const result = await onSubmit(normalized, marketingOptIn);
    if (!result.success) {
      setSubmitErrorCode(result.errorCode ?? "UNKNOWN");
    }
  };

  return (
    <div
      className={
        bare
          ? "relative flex flex-col gap-4 md:gap-6"
          : "relative flex flex-col gap-4 md:gap-6 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8"
      }
    >
      {bare ? (
        <>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="absolute right-0 top-0 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F3F3] text-ink-60"
            >
              <span className="font-material-symbols text-[20px] leading-none">
                close
              </span>
            </button>
          )}
          {showLanguageToggle && (
            <div className="flex items-center gap-3 self-end">
              <LanguageToggle />
            </div>
          )}
        </>
      ) : onClose ? (
        <div className="flex items-center gap-3 self-end">
          {showLanguageToggle && <LanguageToggle />}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F2F3F3] text-ink-60"
          >
            <span className="font-material-symbols text-[20px] leading-none">
              close
            </span>
          </button>
        </div>
      ) : (
        showLanguageToggle && <LanguageToggle />
      )}

      <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
        {headingText}
      </h1>
      <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
        {subtitleText}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="shared-login-phone"
            className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60"
          >
            {fieldLabelText}
          </label>
          <input
            id="shared-login-phone"
            aria-label={fieldLabelText}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder={t("phone.phonePlaceholder")}
            inputMode="tel"
            className="h-14 rounded-2xl border-2 border-ink-12 bg-app px-4 font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100 placeholder:text-ink-40"
          />
        </div>

        {showFormatError && (
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
            {t("phone.errInvalidFormat")}
          </p>
        )}

        {submitErrorCode && (
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
            {submitErrorCode === RATE_LIMITED_CODE
              ? t("phone.errTooManySends")
              : submitErrorCode === RETRY_WAIT_NOT_REACHED_CODE
                ? t("phone.errRetryWaitNotReached")
                : t("phone.errSendFailed")}
          </p>
        )}

        {showMarketingConsent && (
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={marketingOptIn}
              onChange={(e) => setMarketingOptIn(e.target.checked)}
              aria-label={t("phone.marketingOptIn")}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-ink-12"
            />
            <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
              {t("phone.marketingOptIn")}
            </span>
          </label>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[16px]/[20px] font-bold tracking-normal text-white ${
            isSubmitting ? "cursor-not-allowed bg-gray-300" : "bg-primary-100"
          }`}
        >
          {isSubmitting ? t("phone.submitting") : t("phone.submit")}
        </button>

        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {footer}
        </p>
      </form>
    </div>
  );
};
